import React, { useState, useCallback, useEffect, useRef } from "react";
import "../socialHub.css";
import { Badge, Form, InputGroup, Button, Image, Modal } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import {
  FaHeart,
  FaHeartCrack,
  FaRegHeart,
  FaRegComment,
  FaEye,
  FaCommentDots,
  FaPaperPlane,
  FaRegBookmark,
  FaEllipsisVertical,
  FaCheck,
} from "react-icons/fa6";
import Select from "react-select";

import BirthImage from "../../../assets/Images/img (2).png";
import Dummy from "../../../assets/Images/dummy.jpeg";
import { DummyIcon, DummyImage } from "../../../components/icons/Icons";
import { announceEmpListPath } from "../../../services/config";
import { ancemntGetDataThird } from "../../../redux/slices/announcementSlice";
import { useDispatch, useSelector } from "react-redux";

/* ── Share Modal Component ── (MOVED OUTSIDE PostCard) */
const ShareModal = ({ show, onClose, postId, onShare, employeeList }) => {
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const handleShare = () => {
    if (selectedEmployees.length === 0) {
      toast.warning("Please select at least one employee");
      return;
    }
    onShare?.(postId, selectedEmployees.map(e => e.value));
    setSelectedEmployees([]);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="md" dialogClassName="share-modal">
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="modal-title">Share Announcement</Modal.Title>
          <div className="modal-subtitle">Quickly share this announcement with selected employees</div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <label style={{ color: "#9ca3af", fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "8px" }}>
          Select Employees to Share With
        </label>
        <Select
          isMulti
          options={employeeList || []}
          value={selectedEmployees}
          onChange={setSelectedEmployees}
          placeholder="🔍 Search and select employees..."
          styles={{
            control: (base) => ({
              ...base,
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(134,96,248,0.12)",
              color: "#fff",
              minHeight: 44,
              boxShadow: 'none',
              borderRadius: 8,
            }),
            menu: (base) => ({
              ...base,
              background: "rgba(16,14,26,0.96)",
              borderRadius: 14,
              boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
            }),
            option: (base, state) => ({
              ...base,
              background: state.isSelected
                ? "rgba(134,96,248,0.35)"
                : state.isFocused
                ? "rgba(134,96,248,0.18)"
                : "rgba(16,14,26,0.96)",
              color: "#fff",
              cursor: "pointer",
              transition: "background 0.2s ease",
            }),
            multiValue: (base) => ({
              ...base,
              background: "rgba(134,96,248,0.3)",
            }),
            multiValueLabel: (base) => ({
              ...base,
              color: "#c4b5fd",
            }),
            menuList: (base) => ({
              ...base,
              maxHeight: 220,
              paddingTop: 4,
              paddingBottom: 4,
            }),
          }}
          classNamePrefix="share-select"
          className="share-select-control"
        />
      </Modal.Body>
      <Modal.Footer>
        <div style={{ display: 'flex', gap: 10, width: '100%', justifyContent: 'flex-end' }}>
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-share" onClick={handleShare}><FaCheck /> Share</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

/* ── Helper: convert date string to "X time ago" format ── */
const timeAgo = (dateStr) => {
  if (!dateStr) return "Just now";
  try {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    if (diffMs < 0) return "Just now";
    
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return "Just now";
    if (minutes === 1) return "1 min ago";
    if (minutes < 60) return `${minutes} mins ago`;
    if (hours === 1) return "1 hr ago";
    if (hours < 24) return `${hours} hrs ago`;
    if (days === 1) return "1 day ago";
    if (days < 7) return `${days} days ago`;
    if (weeks === 1) return "1 week ago";
    if (weeks < 5) return `${weeks} weeks ago`;
    if (months === 1) return "1 month ago";
    if (months < 12) return `${months} months ago`;
    if (years === 1) return "1 year ago";
    return `${years} years ago`;
  } catch {
    return "Just now";
  }
};

/* ── Single Instagram-style post card ── */
const PostCard = ({
  post,
  onToggleReaction,
  onAddComment,
  onShareClick,
  onOpenComments,
  interactionCounts,
  taggedEmpList,
  onEditPost,
  onDeletePost
}) => {
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [commentText, setCommentText] = useState("");

  // Get the timestamp from multiple possible field names
  const postTime = post.created_at || post.createdAt || post.posted_at || post.postedAt || post.date || post.timestamp;
  const timeAgoText = timeAgo(postTime);

  const caption = post.announceTypeDesc || post.subject || "";
  const isLong = caption.length > 80;

  // Get post ID - try multiple possible field names
  const postId = post.announcement_id || post.announceId || post.id || post.announce_id;
  
  // Get counts from parent - with debug log
  const counts = interactionCounts?.[postId] || { likes: 0, dislikes: 0, comments: 0, shares: 0, user_reaction: null };

  // Interaction permissions (saved with announcement) — normalize to string "1"/"0"
  const normalizeFlag = (v) => (v === undefined || v === null) ? "1" : String(v);
  const allowLike = normalizeFlag(post.reactionAllowId) === "1";
  const allowDislike = normalizeFlag(post.reactionDislikeId) === "1";
  const allowComments = normalizeFlag(post.commentId) === "1";
  const allowReplies = normalizeFlag(post.repliesId) === "1";

  // Sync local state with backend user_reaction
  useEffect(() => {
    if (counts.user_reaction === "like") {
      setLiked(true);
      setDisliked(false);
    } else if (counts.user_reaction === "dislike") {
      setDisliked(true);
      setLiked(false);
    } else {
      setLiked(false);
      setDisliked(false);
    }
  }, [counts.user_reaction]);

  const handleLikeClick = () => {
    if (!allowLike) return;
    onToggleReaction?.(postId, "like");
  };

  const handleDislikeClick = () => {
    if (!allowDislike) return;
    onToggleReaction?.(postId, "dislike");
  };

  const handleCommentSend = () => {
    if (!allowComments) return;
    if (commentText.trim()) {
      onAddComment?.(postId, commentText);
      setCommentText("");
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  return (
    <div style={{
      background: "rgba(22,22,44,0.97)",
      border: "0.5px solid rgba(255,255,255,0.08)",
      borderRadius: "12px",
      overflow: "hidden",
      marginBottom: "24px",
      maxWidth: "480px",
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
    }}>
      {/* ── Header ── */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{
            width: 42, height: 42, borderRadius: "50%",
            background: "linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
            padding: "2px", flexShrink: 0,
          }}>
            <div style={{
              width: "100%", height: "100%", borderRadius: "50%",
              border: "2px solid rgba(22,22,44,0.97)",
              overflow: "hidden",
            }}>
              {post.adPhoto
                ? <img src={post.adPhoto} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="avatar" />
                : <DummyImage class_name={"font-35 border-radius-50"} />
              }
            </div>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {post.announceTypeIcon && (
                <img src={post.announceTypeIcon} style={{ width: 16, height: 16, borderRadius: "4px" }} alt="type" />
              )}
              <span style={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{post.postedBy || "Jhon"}</span>
              <span style={{
                fontSize: "10px", padding: "1px 7px", borderRadius: "20px",
                background: "rgba(134,96,248,0.2)", color: "#c4b5fd", fontWeight: 500,
              }}>Developer</span>
            </div>
            <div style={{ color: "#6b7280", fontSize: "11px" }}>{timeAgoText}</div>
          </div>
        </div>
        <div style={{ position: 'relative' }} ref={menuRef}>
          <button onClick={() => setMenuOpen(p => !p)} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", padding: "4px" }}>
            <FaEllipsisVertical style={{ fontSize: 16 }} />
          </button>

          {menuOpen && (
                <div style={{ position: 'absolute', right: 0, top: '36px', background: '#0f172a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, boxShadow: '0 8px 20px rgba(0,0,0,0.55)', zIndex: 4000, minWidth: 140, overflow: 'hidden' }}>
                  <button onClick={() => { setMenuOpen(false); onEditPost?.(post); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', textAlign: 'left', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13 }}>✏️ <span style={{ flex: 1 }}>Edit Post</span></button>
                  <button onClick={() => { setMenuOpen(false); onDeletePost?.(postId); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', textAlign: 'left', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13 }}>🗑️ <span style={{ flex: 1 }}>Delete Post</span></button>
                </div>
              )}
        </div>
      </div>

      {/* ── Full-width image ── */}
      <div style={{ width: "100%", aspectRatio: "1 / 1", overflow: "hidden" }}>
        <img
          src={post.adPhoto || Dummy}
          alt="post"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>

      {/* ── Action icons row ── */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px 6px",
      }}>
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <button
            onClick={handleLikeClick}
            disabled={!allowLike}
            style={{ background: "none", border: "none", cursor: allowLike ? "pointer" : "not-allowed", padding: 0, lineHeight: 1, opacity: allowLike ? 1 : 0.5 }}
          >
            <FaHeart style={{ fontSize: 24, color: liked ? "#f43f5e" : "#9ca3af", transition: "transform 0.2s, color 0.2s", transform: liked ? "scale(1.15)" : "scale(1)" }} />
          </button>

          <button
            onClick={handleDislikeClick}
            disabled={!allowDislike}
            style={{ background: "none", border: "none", cursor: allowDislike ? "pointer" : "not-allowed", padding: 0, lineHeight: 1, opacity: allowDislike ? 1 : 0.5 }}
          >
            <FaHeartCrack style={{ fontSize: 20, color: disliked ? "#f87171" : "#9ca3af" }} />
          </button>

          <button 
            onClick={() => allowComments && onOpenComments?.(postId, { allowComments, allowReplies })} 
            disabled={!allowComments}
            style={{ background: "none", border: "none", cursor: allowComments ? "pointer" : "not-allowed", padding: 0, lineHeight: 1, opacity: allowComments ? 1 : 0.5 }}
          >
            <FaRegComment style={{ fontSize: 23, color: "#9ca3af" }} />
          </button>

          <button 
            onClick={() => onShareClick?.(postId)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1 }}
          >
            <FaPaperPlane style={{ fontSize: 21, color: "#9ca3af" }} />
          </button>

          <span style={{ color: "#6b7280", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
            <FaEye style={{ fontSize: 15 }} /> {post.views || 0}
          </span>
        </div>

        <button onClick={() => setBookmarked(p => !p)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1 }}>
          <FaRegBookmark style={{ fontSize: 22, color: bookmarked ? "#8660F8" : "#9ca3af" }} />
        </button>
      </div>

      {/* ── Likes count ── */}
      <div style={{ padding: "0 14px 6px" }}>
        <span style={{ color: "#fff", fontSize: "13px", fontWeight: 600 }}>{counts.likes || 0} likes</span>
      </div>

      {/* ── Caption ── */}
      <div style={{ padding: "0 14px 10px" }}>
        <span style={{ color: "#fff", fontWeight: 600, fontSize: "13px", marginRight: "6px" }}>
          {post.postedBy || "Jhon"}
        </span>
        <span style={{ color: "#d1d5db", fontSize: "13px" }}>
          🎂 {post.subject} — {expanded || !isLong ? caption : caption.slice(0, 80) + "…"}
        </span>
        {isLong && (
          <span onClick={() => setExpanded(p => !p)} style={{ color: "#6b7280", fontSize: "13px", cursor: "pointer", marginLeft: "4px" }}>
            {expanded ? " less" : " more"}
          </span>
        )}
        <div style={{ fontSize: "18px", marginTop: "4px" }}>✨</div>
      </div>

      {/* ── Reactions row ── */}
      <div style={{
        display: "flex", gap: "14px", padding: "0 14px 10px",
        borderTop: "0.5px solid rgba(255,255,255,0.06)", paddingTop: "8px",
      }}>
        <span style={{ color: "#4ade80", display: "flex", alignItems: "center", gap: "4px", fontSize: "13px" }}>
          <FaHeart /> {counts.likes || 0}
        </span>
        <span style={{ color: "#f87171", display: "flex", alignItems: "center", gap: "4px", fontSize: "13px" }}>
          <FaHeartCrack /> {counts.dislikes || 0}
        </span>
        <span style={{ color: "#60a5fa", display: "flex", alignItems: "center", gap: "4px", fontSize: "13px" }}>
          <FaCommentDots /> {counts.comments || 0}
        </span>
        <span style={{ color: "#a855f7", display: "flex", alignItems: "center", gap: "4px", fontSize: "13px" }}>
          <FaPaperPlane /> {counts.shares || 0}
        </span>
      </div>

      {/* ── Comment Input ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: "10px",
        padding: "8px 14px 12px",
        borderTop: "0.5px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
          background: "rgba(255,255,255,0.08)",
        }}>
          <img src={BirthImage} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="avatar" />
        </div>
        <InputGroup style={{ flex: 1 }}>
          <Form.Control
            placeholder={allowComments ? "Write a comment..." : "Comments disabled for this announcement"}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyPress={(e) => { if (e.key === "Enter") handleCommentSend(); }}
            className="comment-input universal-input"
            disabled={!allowComments}
            style={{ fontSize: "12px", background: "transparent", border: "none", color: "#9ca3af", padding: "0 4px", opacity: allowComments ? 1 : 0.6 }}
          />
          <Button 
            variant="link" 
            className="send-btn" 
            onClick={handleCommentSend}
            disabled={!allowComments}
            style={{ padding: 0, cursor: allowComments ? 'pointer' : 'not-allowed' }}
          >
            <FaPaperPlane style={{ color: "#8660F8", fontSize: 15 }} />
          </Button>
        </InputGroup>
      </div>
    </div>
  );
};

/* ── Main export ── */
const MainContent = ({
  socialData,
  onToggleReaction,
  onAddComment,
  onReplyComment,
  onGetComments,
  onGetCounts,
  onShareAnnouncement,
  onOpenComments,
  interactionCounts,
  onEditPost,
  onDeletePost
}) => {
  const dispatch = useDispatch();
  const announceStore = useSelector((state) => state.announce);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePostId, setSharePostId] = useState(null);
  const [taggedEmpList, setTaggedEmpList] = useState([]);
  const user = JSON.parse(localStorage.getItem("user_data")) || {};
  
  const fetchedCountsRef = useRef({});

  useEffect(() => {
    const getEmployeeList = () => {
      dispatch(ancemntGetDataThird({
        path: announceEmpListPath,
        data: { userId: user.user_id }
      }));
    };
    getEmployeeList();
  }, [dispatch, user.user_id]);

  useEffect(() => {
    if (Array.isArray(socialData) && socialData.length > 0 && onGetCounts) {
      socialData.forEach(post => {
        const postId = post.announcement_id || post.announceId || post.id;
        if (postId && !fetchedCountsRef.current[postId]) {
          fetchedCountsRef.current[postId] = true;
          console.log(`🔍 Fetching counts for post: ${postId}`);
          onGetCounts(postId);
        }
      });
    }
  }, [socialData, onGetCounts]);

  useEffect(() => {
    console.log("📊 MainContent received interactionCounts:", interactionCounts);
  }, [interactionCounts]);

  useEffect(() => {
    if (announceStore?.ancemntGetThirdSuccess && announceStore.ancemntGetThirdData) {
      const empList = announceStore.ancemntGetThirdData.map((item) => ({
        value: item.staffId,
        label: item.fullName || item.name,
      }));
      setTaggedEmpList(empList);
    }
  }, [announceStore]);

  const handleShareClick = (postId) => {
    setSharePostId(postId);
    setShowShareModal(true);
  };

  const handleShareSubmit = (postId, employeeIds) => {
    onShareAnnouncement?.(postId, employeeIds);
    setShowShareModal(false);
    setSharePostId(null);
  };

  if (!Array.isArray(socialData) || socialData.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
        No announcements available
      </div>
    );
  }

  return (
    <>
      <div className="main-content-hub" style={{ padding: "16px 8px" }}>
        {socialData.map((post) => {
          const postId = post.announcement_id || post.announceId || post.id;
          return (
            <PostCard 
              key={postId}
              post={post} 
              onToggleReaction={onToggleReaction}
              onAddComment={onAddComment}
              onShareClick={handleShareClick}
              onOpenComments={onOpenComments}
              interactionCounts={interactionCounts}
              taggedEmpList={taggedEmpList}
              onEditPost={onEditPost}
              onDeletePost={onDeletePost}
            />
          );
        })}
      </div>

      <ShareModal 
        show={showShareModal}
        onClose={() => setShowShareModal(false)}
        postId={sharePostId}
        onShare={handleShareSubmit}
        employeeList={taggedEmpList}
      />
    </>
  );
};

export default MainContent;